import { useEffect, useState, useCallback, useRef } from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck, RefreshCw, Activity, Database, HardDrive, Zap, Mail,
  CreditCard, AlertTriangle, CheckCircle2, PlayCircle, Trash2, Eye, Bot, Send,
} from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";

const SECRET_STORAGE_KEY = "nexus_admin_health_secret";

/**
 * /admin/health — one-pane operational dashboard.
 *
 * Surfaces the output of `GET /api/admin/health` plus quick actions:
 *  - Regenerate tour narration MP3s
 *  - Run prod cleanup (dry-run preview)
 *  - View tour analytics funnel
 *  - List all users (for picking cleanup keep-set)
 */
export default function AdminHealthPage() {
  const [secret, setSecret] = useState("");
  const [unlocked, setUnlocked] = useState(false);
  const [health, setHealth] = useState(null);
  const [stats, setStats] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const stored = sessionStorage.getItem(SECRET_STORAGE_KEY);
    if (stored) { setSecret(stored); setUnlocked(true); }
  }, []);

  const fetchAll = useCallback(async (sec) => {
    const s = encodeURIComponent(sec || secret);
    const [h, st] = await Promise.all([
      fetch(`${API}/admin/health?secret=${s}`).then((r) => (r.ok ? r.json() : Promise.reject(r.status))),
      fetch(`${API}/tour/stats?secret=${s}`).then((r) => (r.ok ? r.json() : null)),
    ]);
    setHealth(h);
    setStats(st);
  }, [secret]);

  const tryUnlock = async () => {
    if (!secret.trim()) return;
    setBusy(true);
    try {
      await fetchAll(secret.trim());
      setUnlocked(true);
      sessionStorage.setItem(SECRET_STORAGE_KEY, secret.trim());
    } catch (e) {
      toast.error(e === 401 ? "Wrong secret" : `Error ${e}`);
    } finally {
      setBusy(false);
    }
  };

  const regenerateTour = async () => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/tour/regenerate?secret=${encodeURIComponent(secret)}`, { method: "POST" });
      if (!r.ok) throw new Error(String(r.status));
      const data = await r.json();
      toast.success(`Tour narration regenerated · ${Object.keys(data.generated).length} chapters`);
      await fetchAll();
    } catch (e) { toast.error(`Failed: ${e.message}`); }
    setBusy(false);
  };

  const refresh = async () => {
    setBusy(true);
    try { await fetchAll(); toast.success("Refreshed"); }
    catch (e) { toast.error(`Failed: ${e}`); }
    setBusy(false);
  };

  if (!unlocked) return <UnlockGate secret={secret} setSecret={setSecret} tryUnlock={tryUnlock} busy={busy} />;
  if (!health) return <div className="min-h-screen flex items-center justify-center text-[#64748B]">Loading…</div>;

  const ok = health.status === "ok";

  return (
    <div className="min-h-screen bg-[#F8FAFF] py-8 px-4 sm:px-8">
      <div className="max-w-[1300px] mx-auto">
        {/* Header */}
        <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
          <div>
            <div className="text-xs uppercase tracking-[0.25em] text-[#1877F2] mb-1">system</div>
            <h1 className="font-display text-3xl font-bold tracking-tight">Health Dashboard</h1>
            <div className="text-sm text-[#64748B] mt-1">Live snapshot of integrations, storage, and product analytics.</div>
          </div>
          <div className="flex items-center gap-2">
            <button
              data-testid="health-refresh"
              onClick={refresh}
              disabled={busy}
              className="nexus-btn-secondary text-sm flex items-center gap-2"
            >
              <RefreshCw size={14} className={busy ? "animate-spin" : ""} /> Refresh
            </button>
          </div>
        </div>

        {/* Verdict ribbon */}
        <div
          className={`mb-6 rounded-2xl px-5 py-4 flex items-center gap-3 ${
            ok ? "bg-emerald-50 border border-emerald-200" : "bg-amber-50 border border-amber-200"
          }`}
        >
          {ok ? (
            <CheckCircle2 className="text-emerald-600" size={20} />
          ) : (
            <AlertTriangle className="text-amber-600" size={20} />
          )}
          <div className="flex-1">
            <div className={`font-display font-bold ${ok ? "text-emerald-800" : "text-amber-800"}`}>
              {ok ? "All systems nominal" : `${health.issues.length} issue(s) detected`}
            </div>
            {!ok && (
              <ul className="text-sm text-amber-700 mt-1 list-disc ml-5">
                {health.issues.map((i, idx) => <li key={`issue-${idx}`}>{i}</li>)}
              </ul>
            )}
          </div>
        </div>

        {/* Card grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {/* Env */}
          <Card title="Environment" icon={ShieldCheck} accent="#1877F2">
            {Object.entries(health.env).map(([k, v]) => (
              <Row key={k} k={k} v={typeof v === "boolean" ? (v ? "configured" : "missing") : v} ok={v && v !== "(unset)"} />
            ))}
          </Card>

          {/* Stripe */}
          <Card title="Stripe" icon={CreditCard} accent="#DD2A7B">
            <Row k="API key" v={health.stripe.api_key_present ? "present" : "missing"} ok={health.stripe.api_key_present} />
            <Row
              k="Connect platform"
              v={String(health.stripe.connect_enabled)}
              ok={health.stripe.connect_enabled === true}
            />
            {health.stripe.error && (
              <div className="text-xs text-red-600 mt-2 font-mono break-all">{health.stripe.error}</div>
            )}
          </Card>

          {/* Storage */}
          <Card title="Storage" icon={HardDrive} accent="#F59E0B">
            <Row k="User upload files" v={`${health.storage.user_files_count} · ${fmtBytes(health.storage.user_files_bytes)}`} ok />
            <Row k="Tour narration MP3s" v={`${health.storage.tour_files_count} · ${fmtBytes(health.storage.tour_files_bytes)}`} ok={health.storage.tour_files_count > 0} />
            <Row k="Orphan posts (broken media)" v={String(health.storage.orphan_posts_estimate)} ok={health.storage.orphan_posts_estimate === 0} />
            <button
              data-testid="regenerate-tour"
              onClick={regenerateTour}
              disabled={busy}
              className="mt-3 w-full text-xs px-3 py-2 rounded-full bg-[#1877F2] text-white font-bold flex items-center justify-center gap-2 hover:brightness-110 transition disabled:opacity-50"
            >
              <PlayCircle size={12} /> Regenerate tour narration
            </button>
          </Card>

          {/* Tour analytics */}
          <Card title="Product Tour" icon={Zap} accent="#7B2CBF">
            <Row k="Narration ready" v={String(health.tour.narration_ready)} ok={health.tour.narration_ready} />
            <Row k="Events logged" v={String(health.tour.events_logged)} ok />
            {stats && (
              <>
                <div className="mt-3 pt-3 border-t border-slate-200">
                  <div className="text-xs uppercase tracking-[0.2em] text-[#7B2CBF] mb-2">funnel</div>
                  <Row k="Sessions started" v={String(stats.unique_sessions.started)} ok />
                  <Row k="Completed (%)" v={`${stats.funnel.start_to_complete_pct}%`} ok={stats.funnel.start_to_complete_pct >= 30} />
                  <Row k="Signup after tour" v={`${stats.unique_sessions.signup_after_tour} (${stats.funnel.complete_to_signup_pct}%)`} ok={stats.unique_sessions.signup_after_tour > 0} />
                </div>
              </>
            )}
          </Card>

          {/* Digest */}
          <Card title="Weekly Digest" icon={Mail} accent="#00E5FF">
            {health.last_digest_run ? (
              <>
                <Row k="Last run" v={fmtDate(health.last_digest_run.ran_at)} ok />
                <Row k="Active creators" v={String(health.last_digest_run.active_creators ?? 0)} ok />
                <Row k="Emails sent" v={String(health.last_digest_run.emails_sent ?? "—")} ok />
                <Row k="Health" v={health.last_digest_run.health?.healthy ? "healthy" : (health.last_digest_run.health?.reason || "degraded")} ok={health.last_digest_run.health?.healthy} />
              </>
            ) : (
              <div className="text-sm text-[#64748B]">No digest runs recorded yet.</div>
            )}
          </Card>

          {/* Mongo */}
          <Card title="MongoDB collections" icon={Database} accent="#0EA5E9">
            <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
              {Object.entries(health.mongo_collections)
                .sort(([, a], [, b]) => b - a)
                .map(([k, v]) => (
                  <div key={k} className="flex justify-between border-b border-slate-100 py-0.5">
                    <span className="text-[#64748B] truncate">{k}</span>
                    <span className="font-bold text-[#0F172A] tabular-nums">{v}</span>
                  </div>
                ))}
            </div>
          </Card>
        </div>

        {/* AI Ops diagnostic console — Claude with the live health JSON as context. */}
        <AIDiagnosticCard health={health} stats={stats} />

        {/* Quick actions */}
        <div className="mt-6 glass-heavy rounded-2xl p-5">
          <div className="flex items-center gap-2 mb-3">
            <Activity size={16} className="text-[#1877F2]" />
            <div className="font-display font-bold">Quick actions</div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            <a
              href={`${API}/admin/users?secret=${encodeURIComponent(secret)}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="health-action-users"
              className="glass rounded-xl p-3 hover:scale-[1.01] transition flex items-center gap-2"
            >
              <Eye size={14} className="text-[#7B2CBF]" /> View all users (JSON)
            </a>
            <a
              href={`${API}/admin/cleanup-prod-data/preview?secret=${encodeURIComponent(secret)}&keep=REPLACE_WITH_YOUR_USER_ID`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="health-action-cleanup-preview"
              className="glass rounded-xl p-3 hover:scale-[1.01] transition flex items-center gap-2"
            >
              <Trash2 size={14} className="text-[#DD2A7B]" /> Cleanup dry-run preview
            </a>
            <a
              href="/admin/digest"
              data-testid="health-action-digest"
              className="glass rounded-xl p-3 hover:scale-[1.01] transition flex items-center gap-2"
            >
              <Mail size={14} className="text-[#00E5FF]" /> Digest dashboard
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

function UnlockGate({ secret, setSecret, tryUnlock, busy }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFF] px-4">
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="glass-heavy rounded-3xl p-8 max-w-md w-full">
        <ShieldCheck size={32} className="text-[#1877F2] mb-3" />
        <div className="font-display font-bold text-2xl mb-1">Health Dashboard</div>
        <div className="text-sm text-[#64748B] mb-5">Enter the admin secret to continue.</div>
        <input
          type="password"
          data-testid="health-secret"
          value={secret}
          onChange={(e) => setSecret(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && tryUnlock()}
          placeholder="ADMIN_SECRET"
          className="w-full mb-3 px-4 py-3 rounded-xl bg-white border border-slate-200 focus:border-[#1877F2] outline-none"
        />
        <button
          data-testid="health-unlock"
          onClick={tryUnlock}
          disabled={busy || !secret.trim()}
          className="w-full nexus-btn-primary text-sm"
        >
          {busy ? "Unlocking…" : "Unlock"}
        </button>
      </motion.div>
    </div>
  );
}

function Card({ title, icon: Icon, accent, children }) {
  return (
    <div className="glass-heavy rounded-2xl p-5">
      <div className="flex items-center gap-2 mb-3">
        <Icon size={16} style={{ color: accent }} />
        <div className="font-display font-bold">{title}</div>
      </div>
      <div>{children}</div>
    </div>
  );
}

function Row({ k, v, ok }) {
  return (
    <div className="flex items-center justify-between py-1 border-b border-slate-100 last:border-0">
      <span className="text-xs text-[#64748B]">{k}</span>
      <span className={`text-xs font-bold tabular-nums ${ok ? "text-emerald-700" : "text-amber-700"}`}>{v}</span>
    </div>
  );
}

function fmtBytes(n) {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleString(); } catch { return s; }
}



/**
 * AI Ops Diagnostic — Claude with the live health JSON injected as context so
 * the operator can ask "why is X broken?" and get a grounded answer instead of
 * hallucinations. Reuses the same `/ai/chat/stream` SSE endpoint as the
 * assistant dock so we get streaming token-by-token feedback.
 */
function AIDiagnosticCard({ health, stats }) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const ask = async () => {
    if (!input.trim() || busy) return;
    const q = input.trim();
    setInput("");
    setMessages((m) => [...m, { role: "user", text: q }, { role: "assistant", text: "" }]);
    setBusy(true);

    // Context: trim health + tour stats down to a focused JSON to keep token cost low.
    const context = {
      status: health?.status,
      issues: health?.issues || [],
      env_configured: Object.fromEntries(Object.entries(health?.env || {}).map(([k, v]) => [k, !!v])),
      stripe: health?.stripe,
      storage: health?.storage,
      mongo_top_collections: Object.fromEntries(
        Object.entries(health?.mongo_collections || {}).sort(([, a], [, b]) => b - a).slice(0, 10)
      ),
      last_digest_run: health?.last_digest_run,
      tour_funnel: stats?.funnel,
    };
    const system = `You are NEXUS Ops — an SRE assistant for the operator running this platform. The user will ask diagnostic questions. The live system snapshot is provided below. Answer concretely with 1-3 bullet points and a recommended next action. JSON snapshot:\n${JSON.stringify(context)}`;

    try {
      const res = await fetch(`${API}/ai/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message: q, provider: "anthropic", system }),
      });
      if (!res.ok || !res.body) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Diagnostic stream failed");
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const block of parts) {
          let evt = "message"; let data = "";
          for (const line of block.split("\n")) {
            if (line.startsWith("event:")) evt = line.slice(6).trim();
            else if (line.startsWith("data:")) data += line.slice(5).trim();
          }
          if (!data) continue;
          try {
            const parsed = JSON.parse(data);
            if (evt === "delta" && parsed.content) {
              setMessages((m) => {
                const arr = [...m];
                arr[arr.length - 1] = { role: "assistant", text: arr[arr.length - 1].text + parsed.content };
                return arr;
              });
            }
          } catch {/* ignore */}
        }
      }
    } catch (e) {
      toast.error(e.message || "Diagnostic unreachable");
    } finally {
      setBusy(false);
    }
  };

  const suggestions = [
    "What's the most likely cause of the issues you see?",
    "Which Mongo collection should I clean up first?",
    "Why is the tour funnel showing a drop-off?",
  ];

  return (
    <div className="mt-6 glass-heavy rounded-2xl p-5" data-testid="ai-ops-console">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 mb-3 group"
      >
        <Bot size={16} className="text-[#1877F2]" />
        <div className="font-display font-bold text-left">AI Ops console</div>
        <div className="text-xs text-[#64748B] ml-2">Claude diagnoses with live system context</div>
        <div className="ml-auto text-xs text-[#1877F2] group-hover:underline">{open ? "Hide" : "Open"}</div>
      </button>

      {open && (
        <>
          <div
            ref={scrollRef}
            data-testid="ai-ops-log"
            className="max-h-72 overflow-y-auto rounded-xl bg-slate-50 p-3 space-y-2.5 mb-3"
          >
            {messages.length === 0 ? (
              <div className="text-xs text-[#64748B]">
                <div className="mb-2">Ask anything about the live system. Try:</div>
                <ul className="space-y-1">
                  {suggestions.map((s) => (
                    <li key={s}>
                      <button
                        data-testid={`ai-ops-suggest`}
                        onClick={() => setInput(s)}
                        className="text-left text-[#1877F2] hover:underline"
                      >
                        → {s}
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              messages.map((m, i) => (
                <div key={`msg-${i}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                  <div
                    className={`max-w-[88%] px-3 py-2 rounded-xl text-sm whitespace-pre-wrap leading-relaxed ${
                      m.role === "user"
                        ? "bg-[#1877F2] text-white"
                        : "bg-white border border-slate-200 text-[#0F172A]"
                    }`}
                  >
                    {m.text || (busy && i === messages.length - 1 ? <span className="text-[#94A3B8]">…</span> : "")}
                  </div>
                </div>
              ))
            )}
          </div>
          <div className="flex items-center gap-2">
            <input
              data-testid="ai-ops-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && ask()}
              placeholder="What should I look at first?"
              className="nexus-input"
            />
            <button
              data-testid="ai-ops-send"
              onClick={ask}
              disabled={busy || !input.trim()}
              className="nexus-btn-primary disabled:opacity-50 !py-2.5 !px-4"
              aria-label="Ask"
            >
              <Send size={14} />
            </button>
          </div>
        </>
      )}
    </div>
  );
}
